/**
 * Provider Factory
 *
 * Creates LLM providers based on configuration
 */

import { LLMProvider } from './base';
import { OpenAIProvider } from './openai';
import { AnthropicProvider } from './anthropic';
import { GroqProvider } from './groq';
import { GeminiProvider } from './gemini';
import { OpenRouterProvider } from './openrouter';

import { pluginLogger } from '@/lib/platform/utils/logger';

export interface AIConfig {
  provider: 'openai' | 'anthropic' | 'groq' | 'gemini' | 'openrouter';
  model?: string;
  // Store API keys separately per provider
  openaiApiKey?: string;
  anthropicApiKey?: string;
  groqApiKey?: string;
  geminiApiKey?: string;
  openrouterApiKey?: string;
}

export function createProvider(config: AIConfig): LLMProvider | null {
  if (!config.provider) {
    pluginLogger.error('[AI Insights] No provider specified');
    return null;
  }

  pluginLogger.log(
    `[AI Insights] Creating provider: ${config.provider} with model: ${config.model || '(default)'}`
  );

  switch (config.provider) {
    case 'openai':
      if (!config.openaiApiKey) {
        pluginLogger.error('[AI Insights] OpenAI provider requires API key');
        return null;
      }
      return new OpenAIProvider(config.openaiApiKey, config.model || 'gpt-4o-mini');

    case 'anthropic':
      if (!config.anthropicApiKey) {
        pluginLogger.error('[AI Insights] Anthropic provider requires API key');
        return null;
      }
      return new AnthropicProvider(
        config.anthropicApiKey,
        config.model || 'claude-3-5-sonnet-20241022'
      );

    case 'groq': {
      if (!config.groqApiKey) {
        pluginLogger.error('[AI Insights] Groq provider requires API key');
        return null;
      }
      // Groq decommissioned these models on 2026-08-16. Remap stored configs
      // to the recommended replacements so existing organizations keep working.
      const retiredGroqModels: Record<string, string> = {
        'llama-3.3-70b-versatile': 'openai/gpt-oss-120b',
        'llama-3.1-8b-instant': 'openai/gpt-oss-20b',
      };
      const groqModel = config.model
        ? (retiredGroqModels[config.model] ?? config.model)
        : 'openai/gpt-oss-120b';
      return new GroqProvider(config.groqApiKey, groqModel);
    }

    case 'gemini':
      if (!config.geminiApiKey) {
        pluginLogger.error('[AI Insights] Gemini provider requires API key');
        return null;
      }
      return new GeminiProvider(config.geminiApiKey, config.model || 'gemini-2.5-flash');

    case 'openrouter':
      if (!config.openrouterApiKey) {
        pluginLogger.error('[AI Insights] OpenRouter provider requires API key');
        return null;
      }
      return new OpenRouterProvider(
        config.openrouterApiKey,
        config.model || 'meta-llama/llama-3.1-8b-instruct:free'
      );

    default:
      pluginLogger.error(`[AI Insights] Unknown provider: ${config.provider}`);
      return null;
  }
}
